import { useContext } from "react";
import { DrugCost, DrugCostInfo, PharmacyInfo } from "../../../@types";
import { AppStore } from "../../../app/store";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import { useTranslate } from "../../../helpers";

/**
 * Get formatted pharmacy info from selected pharmacies and drug cost info
 * @param drugCostInfo
 * @returns - Formatted pharmacy info for selected pharmacies
 */
export const useGetPharmacies = (
  drugCostInfo: DrugCostInfo
): PharmacyInfo[] => {
  const t = useTranslate();
  const { state } = useContext(AppStore.AppContext);
  const selectedPharmacies = state.pharmacies;
  const includesMailOrder = hasMailOrderPharmacy(state);

  const { costs: pharmacyCosts } = drugCostInfo;

  const pharmacies = [] as PharmacyInfo[];
  pharmacyCosts.forEach(thisPharmacy => {
    if (thisPharmacy.npi || includesMailOrder) {
      const { npi, in_network, mail_order, preferred, ltc } = thisPharmacy;
      const name =
        selectedPharmacies.find(pharm => pharm.npi === npi)?.name ||
        t("plan_details.pharmacies.mail_order_pharmacy");

      pharmacies.push({
        npi,
        name,
        in_network,
        mail_order,
        preferred,
        ltc,
      });
    }
  });
  return pharmacies;
};

/**
 * Derive a number from pharmacy drug costs or return "N/A" if any cost is `NaN`
 * @param arr - DrugCost array
 * @param prop - Enum of strings representing cost phases (@TODO - update with
 *               a type or interface)
 * @returns - Cost as a number or "N/A"
 */
export const sumBy = (arr: DrugCost[], prop: string): string | number => {
  return arr.reduce((prev, curr) => {
    if (isNaN(curr[prop])) {
      return "N/A";
    }
    return prev + curr[prop];
  }, 0);
};

/**
 * Get pharmacy name from NPI ID
 * @param npi - Pharmacy identifier
 * @returns - Pharmacy name
 */
export const useGetPharmacyName = (npi: string): string => {
  const t = useTranslate();
  const {
    state: { pharmacies: selectedPharmacies },
  } = useContext(AppStore.AppContext);
  if (npi === "") {
    return t("plan_search_prefs.mail_order_pharmacy");
  }

  const pharmacy = selectedPharmacies.find(p => p.npi === npi);

  return pharmacy ? pharmacy.name : "N/A";
};
